import React from 'react';
import * as motion from 'motion/react-client';
import { AnimatePresence } from 'motion/react';

import { FilledDot } from './MobileNavigationStep';
import MobileNavigationStep from './MobileNavigationStep';
import Header from 'modules/views/shared/Header';
import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';
import { ReactComponent as ArrowUp } from 'assets/icons/ArrowUp.svg';

import { useStyles } from '../styles';

interface IMobileWizardProps {
  steps: Array<{
    title: string;
    content: React.ReactNode;
    isActive?: boolean;
    isCompleted?: boolean;
    isClickable?: boolean;
    disabled?: boolean;
    fields?: {
      name: string;
      id: string;
      isRequired?: boolean;
      isCompleted?: boolean;
    }[];
    icon: React.ReactNode;
    absoluteStepIndex?: number;
  }>;
  currentStepIndex: number;
  currentStep: { title: string };
  isExpanded: boolean;
  disableNavigation?: boolean;
  styleClass?: string;
  contentContainerStyleClass?: string;
  contentStyleClass?: string;
  showOnlineWarning?: boolean;
  onToggleExpanded: () => void;
  onStepChange?: (stepIndex: number) => void;
  onClose: () => void;
  Content: React.ReactNode;
}

const MobileWizard: React.FC<IMobileWizardProps> = ({
  steps,
  currentStepIndex,
  currentStep,
  isExpanded,
  disableNavigation,
  styleClass,
  contentContainerStyleClass,
  contentStyleClass,
  showOnlineWarning,
  onToggleExpanded,
  onStepChange,
  onClose,
  Content,
}) => {
  const classes = useStyles();

  // Render mobile navigation steps
  const renderMobileNavigationSteps = () =>
    steps.map((step, index) => {
      const absoluteIndex = step.absoluteStepIndex ?? index;
      return (
        <MobileNavigationStep
          key={absoluteIndex}
          title={step.title}
          isActive={absoluteIndex === currentStepIndex}
          step={step}
          isCompleted={step.isCompleted}
          isDisabled={(disableNavigation && absoluteIndex !== currentStepIndex) || step.disabled || !step.isClickable}
          isLastStep={index === steps.length - 1}
          onClick={() => {
            if (absoluteIndex !== currentStepIndex && !disableNavigation && !step.disabled && step.isClickable) {
              onStepChange?.(absoluteIndex); // Use absolute step index
              onClose();
            }
          }}
        />
      );
    });

  return (
    <div className={`${classes.mobileWizardContainer} ${styleClass || ''}`}>
      {/* Mobile Backdrop - only show when expanded */}
      {isExpanded && <div className={classes.mobileBackdrop} onClick={onClose} />}

      {/* Mobile Navigation Bar */}
      <div className={classes.mobileNavBar}>
        <div className={classes.mobileNavHeader} onClick={onToggleExpanded}>
          <div className={classes.mobileCurrentStepIndicator}>
            <div className={classes.mobileCurrentStepIcon}>
              <FilledDot />
            </div>
            <span className={classes.mobileCurrentStepTitle}>{currentStep?.title}</span>
          </div>
          <div className={classes.mobileExpandIcon}>{isExpanded ? <ArrowUp /> : <ArrowDown />}</div>
        </div>

        {/* Divider below current step header - only show when expanded */}
        {isExpanded && <div className={classes.mobileHeaderDivider} />}

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{
                duration: 0.2,
                ease: 'linear',
              }}
            >
              <div className={classes.mobileExpandedNavigation}>
                <div className={classes.mobileNavigationSteps}>{renderMobileNavigationSteps()}</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Content Area */}
      <div
        className={`${classes.mobileContentContainer} ${showOnlineWarning ? classes.mobileContentContainerWithWarning : ''} ${contentContainerStyleClass || ''}`}
        style={{ overflowY: isExpanded ? 'hidden' : 'auto' }}
      >
        <Header styleClass={classes.header} showOnlineWarning={showOnlineWarning} />
        <div className={`${classes.mobileContent} ${contentStyleClass || ''}`}>{Content}</div>
      </div>
    </div>
  );
};

export default MobileWizard;
